import {
  IconArrowNarrowRight,
  IconClipboardList,
  IconDeviceMobileMessage,
  IconGift,
  IconHome,
  IconNews,
  IconPhoneCall,
  IconReport,
  IconShieldLock,
  IconTicket,
  IconUser,
  IconUserShield,
  IconWallet,
} from "@tabler/icons-react";
import { Avatar, Flex, Image, Layout, Menu } from "antd";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import logo from "../../assets/images/logo-white.svg";
import ConfirmModal from "../../components/modal/ConfirmModal";
import { useToast } from "../../components/toast/toast";
import { useAuth } from "../../store/context/AuthContext";
import { hasPerm } from "../../utilities/permission";

const { Sider } = Layout;

const allow = (me, cfg = {}) => (cfg.perm ? hasPerm(me, cfg.perm) : !!me);

/** ---------------- Меню конфиг ---------------- */
const NAV = [
  { key: "/dashboard", label: "Дашбоард", icon: "home" },

  {
    key: "lottery-group",
    label: "Сугалаа",
    icon: "lottery",
    perm: "READ_LOTTERY",
    children: [
      {
        key: "create-lottery",
        label: "Сугалаа үүсгэх",
      },
      {
        key: "/lottery/active",
        label: "Идэвхтэй сугалаа",
      },
      {
        key: "/lottery/completed",
        label: "Дууссан сугалаа",
      },
      {
        key: "/lottery/draft",
        label: "Draft сугалаа",
      },
    ],
  },

  {
    key: "ticket-group",
    label: "Тасалбар",
    icon: "ticket",
    perm: ["VIEW_TICKET", "VIEW_COMPLAINT"],
    children: [
      {
        key: "/ticket/engagement",
        label: "Хэрэглэгчийн оролцоо",
      },
      {
        key: "/ticket/feedback",
        label: "Гомдол, асуудал",
      },
    ],
  },
  {
    key: "/report",
    label: "Тайлан",
    icon: "report",
    perm: "REPORT_LOTTERY",
  },

  {
    key: "/reward",
    label: "Урамшуулал",
    icon: "reward",
    perm: "VIEW_REWARDS",
  },

  {
    key: "bank-group",
    label: "Банк",
    icon: "bank",
    perm: ["VIEW_BANK", "VIEW_BANK_STATEMENT"],
    children: [
      { key: "/bank/account", label: "Банкны данс" },
      {
        key: "/bank/statement",
        label: "Дансны хуулга",
      },
    ],
  },

  {
    key: "/news",
    label: "Мэдээ мэдээлэл",
    icon: "news",
    perm: "VIEW_NEWS",
  },
  {
    key: "/sms",
    label: "Мессеж",
    icon: "sms",
    perm: "READ_SMS",
  },
  {
    key: "/call-center",
    label: "Дуудлагын төв",
    icon: "call-center",
    perm: "VIEW_CALL_LIST",
  },
  {
    key: "/staff",
    label: "Ажилтан",
    icon: "staff",
    perm: "VIEW_USER",
  },
  {
    key: "/roles",
    label: "Эрхийн тохиргоо",
    icon: "roles",
    perm: "VIEW_ROLE",
  },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  // submenu open state
  const [openKeys, setOpenKeys] = useState(() => {
    const path = location.pathname;
    const keys = [];
    if (path.startsWith("/lottery")) keys.push("lottery-group");
    if (path.startsWith("/ticket")) keys.push("ticket-group");
    if (path.startsWith("/bank")) keys.push("bank-group");
    return keys;
  });

  // “шинэ сугалаа” баталгаажуулалт
  const [showModal, setShowModal] = useState(false);
  const submitHandler = async () => {
    const res = await api.create_lottery();
    if (res.status === 201) {
      navigate(`/lottery/draft/${res.data.id}/edit`);
      toast("Амжилттай үүслээ", "success");
    } else {
      // console.error("Error creating lottery:", res);
    }
  };

  // аль цэс идэвхтэй вэ?
  const selectedKey = (() => {
    const path = location.pathname;
    if (path.startsWith("/lottery/active")) return "/lottery/active";
    if (path.startsWith("/lottery/completed")) return "/lottery/completed";
    if (path.startsWith("/lottery/draft")) return "/lottery/draft";
    if (path.startsWith("/report")) return "/report";
    if (path.startsWith("/ticket/feedback")) return "/ticket/feedback";
    if (path.startsWith("/ticket/engagement")) return "/ticket/engagement";
    if (path.startsWith("/dashboard")) return "/dashboard";
    if (path.startsWith("/sms")) return "/sms";
    if (path.startsWith("/staff")) return "/staff";
    if (path.startsWith("/bank/account")) return "/bank/account";
    if (path.startsWith("/bank/statement")) return "/bank/statement";
    if (path.startsWith("/news")) return "/news";
    if (path.startsWith("/call-center")) return "/call-center";
    if (path.startsWith("/reward")) return "/reward";
    if (path.startsWith("/roles")) return "/roles";
    return "/";
  })();

  // NAV-г RBAC-аар filter хийх
  const filteredNav = useMemo(() => {
    const filterTree = (nodes) =>
      nodes
        .filter((n) => allow(user, n))
        .map((n) =>
          n.children ? { ...n, children: filterTree(n.children) } : n,
        )
        .filter((n) => (n.children ? n.children.length > 0 : true));
    return filterTree(NAV);
  }, [user]);

  // icon өнгө
  const renderIcon = (name) => {
    const active = (k) =>
      (k === "/dashboard" && location.pathname === "/dashboard") ||
      (k === "lottery" && location.pathname.startsWith("/lottery")) ||
      (k === "ticket" && location.pathname.startsWith("/ticket")) ||
      (k === "/report" && location.pathname.startsWith("/report")) ||
      (k === "bank" && location.pathname.startsWith("/bank")) ||
      (k === "/news" && location.pathname.startsWith("/news")) ||
      (k === "/sms" && location.pathname === "/sms") ||
      (k === "/staff" && location.pathname === "/staff") ||
      (k === "/roles" && location.pathname.startsWith("/roles")) ||
      (k === "/call-center" && location.pathname.startsWith("/call-center")) ||
      (k === "/reward" && location.pathname.startsWith("/reward"));

    switch (name) {
      case "home":
        return (
          <IconHome
            size={20}
            color={active("/dashboard") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "lottery":
        return (
          <IconClipboardList
            size={20}
            color={active("lottery") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "ticket":
        return (
          <IconTicket
            size={20}
            color={active("ticket") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "report":
        return (
          <IconReport
            size={20}
            color={active("/report") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "reward":
        return (
          <IconGift
            size={20}
            color={active("/reward") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "bank":
        return (
          <IconWallet
            size={20}
            color={active("bank") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "news":
        return (
          <IconNews
            size={20}
            color={active("/news") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "sms":
        return (
          <IconDeviceMobileMessage
            size={20}
            color={active("/sms") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "staff":
        return (
          <IconUserShield
            size={20}
            color={active("/staff") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "call-center":
        return (
          <IconPhoneCall
            size={20}
            color={active("/call-center") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      case "roles":
        return (
          <IconShieldLock
            size={20}
            color={active("/roles") ? "white" : "royalblue"}
            stroke={1.5}
          />
        );
      default:
        return null;
    }
  };

  // AntD Menu items
  const toMenuItems = (nodes) =>
    nodes.map((n) => ({
      key: n.key,
      label: n.label,
      icon: n.icon ? renderIcon(n.icon) : undefined,
      children: n.children ? toMenuItems(n.children) : undefined,
    }));

  // key→config lookup (guard-тай onClick хийхэд)
  const flatMapByKey = useMemo(() => {
    const m = new Map();
    const walk = (nodes) => {
      nodes.forEach((n) => {
        m.set(n.key, n);
        if (n.children) walk(n.children);
      });
    };
    walk(NAV);
    return m;
  }, []);

  return (
    <>
      <Sider
        trigger={null}
        style={{ overflow: "hidden", height: "100vh" }}
        width={230}
      >
        <Flex
          vertical
          style={{
            width: "100%",
            height: "100%",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "20px",
            overflow: "auto",
            scrollbarWidth: "thin",
            padding: "20px 0 10px",
          }}
        >
          <Flex vertical style={{ width: "100%", gap: "20px" }}>
            <Image
              preview={false}
              src={logo}
              width={"80%"}
              alt="logo"
              style={{ alignSelf: "center", margin: "0 10%" }}
            />

            <Menu
              theme="dark"
              mode="inline"
              defaultSelectedKeys={["/"]}
              openKeys={openKeys}
              onOpenChange={(keys) => setOpenKeys(keys)}
              selectedKeys={[selectedKey]}
              onClick={({ key }) => {
                // RBAC guard (харуулсан ч дахин баталгаажуулъя)
                const cfg = flatMapByKey.get(key);
                if (!allow(user, cfg)) {
                  toast("Зөвшөөрөлгүй", "error");
                  return;
                }
                if (key === "create-lottery") {
                  setShowModal(true);
                } else {
                  navigate(key);
                }
              }}
              items={toMenuItems(filteredNav)}
            />
          </Flex>

          <Flex
            align="center"
            justify="space-between"
            gap={"5px"}
            style={{
              padding: "10px",
              margin: "0 10px",
              width: "calc(100% - 20px)",
              backgroundColor: "#0f1e76",
              borderRadius: "8px",
              cursor: "pointer",
            }}
            onClick={() => navigate("/profile")}
          >
            <Flex align="center" gap="10px">
              <Avatar icon={<IconUser size={16} />} />
              <p style={{ fontWeight: 600, color: "#fff", margin: 0 }}>
                {user?.name || "Профайл"}
              </p>
            </Flex>
            <IconArrowNarrowRight color="#fff" size={20} />
          </Flex>
        </Flex>
      </Sider>

      <ConfirmModal
        title="Шинэ сугалаа үүсгэх үү?"
        showModal={showModal}
        setShowModal={setShowModal}
        submitHandler={submitHandler}
      />
    </>
  );
};

export default Sidebar;
